import React from 'react'

import { Paragraph } from '@telus-uds/components-web'
import { ImagesLayout, StyledImage, TallerImageContainer, StyledImageCaption, ImageContainer } from './Styles'
import { convertToCorrectImage } from 'src/blogBuilder/utils/imageConversion'
import BBImage from '../Image'

import type { Asset } from 'contentful'
import type { ImageProp } from '../../utils/types/image'

type ImagesSectionProps = {
  firstImage: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>
  firstImageCaption: string
  secondImage: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>
  secondImageCaption: string
  thirdImage: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>
  thirdImageCaption: string
}

type AllImagesCombined = {
  firstImageConverted: ImageProp
  firstImageCaption: string
  secondImageConverted: ImageProp
  secondImageCaption: string
  thirdImageConverted: ImageProp
  thirdImageCaption: string
}

const renderCaption = (caption: string): JSX.Element => (
  <StyledImageCaption>
    <Paragraph size="small">{caption}</Paragraph>
  </StyledImageCaption>
)

const renderSingleImage = (firstImage: ImageProp, firstImageCaption: string): JSX.Element => {
  return (
    <>
      <BBImage
        data-testid="bb-first-image"
        src={firstImage.url}
        width={firstImage.width}
        height={firstImage.height}
        alt={firstImage.alt}
        placeholder="blur"
        removeWhiteSpace
      />
      {firstImageCaption && renderCaption(firstImageCaption)}
    </>
  )
}

const renderTwoImages = (
  firstImage: ImageProp,
  firstImageCaption: string,
  secondImage: ImageProp,
  secondImageCaption: string
): JSX.Element => {
  return (
    <ImagesLayout alignItems="flex-start">
      <ImageContainer>
        <StyledImage
          data-testid="bb-first-image"
          src={firstImage.url}
          description={firstImage.description}
          width={firstImage.width}
          height={firstImage.height}
          alt={firstImage.alt}
          placeholder="blur"
        />
        {firstImageCaption && renderCaption(firstImageCaption)}
      </ImageContainer>
      <TallerImageContainer spacingOnTop="5rem">
        <StyledImage
          data-testid="bb-second-image"
          src={secondImage.url}
          description={secondImage.description}
          width={secondImage.width}
          height={secondImage.height}
          alt={secondImage.alt}
          placeholder="blur"
        />
        {secondImageCaption && renderCaption(secondImageCaption)}
      </TallerImageContainer>
    </ImagesLayout>
  )
}

const renderThreeImages = ({
  firstImageConverted,
  firstImageCaption,
  secondImageConverted,
  secondImageCaption,
  thirdImageConverted,
  thirdImageCaption,
}: AllImagesCombined): JSX.Element => {
  return (
    <ImagesLayout data-testid="bb-images-section">
      <ImageContainer>
        <StyledImage
          width={firstImageConverted.width}
          height={firstImageConverted.height}
          data-testid="bb-first-image"
          src={firstImageConverted.url}
          description={firstImageConverted.description}
          alt={firstImageConverted.alt}
          placeholder="blur"
        />
        {firstImageCaption && renderCaption(firstImageCaption)}
        <StyledImage
          width={firstImageConverted.width}
          height={firstImageConverted.height}
          data-testid="bb-second-image"
          src={secondImageConverted.url}
          alt={secondImageConverted.alt}
          description={secondImageConverted.description}
          placeholder="blur"
        />
        {secondImageCaption && renderCaption(secondImageCaption)}
      </ImageContainer>
      <TallerImageContainer>
        <StyledImage
          width={firstImageConverted.width}
          height={firstImageConverted.height}
          data-testid="bb-third-image"
          src={thirdImageConverted.url}
          alt={thirdImageConverted.alt}
          description={thirdImageConverted.description}
          placeholder="blur"
        />
        {thirdImageCaption && renderCaption(thirdImageCaption)}
      </TallerImageContainer>
    </ImagesLayout>
  )
}
const ImagesSection = ({
  firstImage,
  firstImageCaption,
  secondImage,
  secondImageCaption,
  thirdImage,
  thirdImageCaption,
}: ImagesSectionProps) => {
  // ignores if there is no images
  if (!firstImage) {
    return <React.Fragment />
  }
  const firstImageConverted: ImageProp = convertToCorrectImage(firstImage)
  const secondImageConverted: ImageProp = secondImage ? convertToCorrectImage(secondImage) : null
  const thirdImageConverted: ImageProp = thirdImage ? convertToCorrectImage(thirdImage) : null

  return (
    <React.Fragment>
      {secondImage &&
        thirdImage &&
        renderThreeImages({
          firstImageConverted,
          firstImageCaption,
          secondImageConverted,
          secondImageCaption,
          thirdImageConverted,
          thirdImageCaption,
        })}
      {secondImage &&
        !thirdImage &&
        renderTwoImages(firstImageConverted, firstImageCaption, secondImageConverted, secondImageCaption)}
      {!secondImage && !thirdImage && renderSingleImage(firstImageConverted, firstImageCaption)}
    </React.Fragment>
  )
}

export default ImagesSection
